import React from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaRegLightbulb, FaRegUser, FaClock } from 'react-icons/fa';
import OrganicPattern from './Patterns';
import { FACIAL_IMAGES } from '../constants/assets';

const Facial = () => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <div className="bg-white">
            {/* Hero Section */}
            <div className="relative h-[30vh] md:h-[50vh] bg-eerie-black overflow-hidden">
    <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url("${FACIAL_IMAGES.hero}")` }}
    >
        <div className="absolute inset-0 bg-gradient-to-b from-eerie-black/90 via-eerie-black/80 to-eerie-black/70" />
    </div>
    
    <OrganicPattern />
    
    <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="relative h-full flex items-center justify-center text-center px-4"
    >
        <div className="max-w-3xl">
            <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="font-magenta text-3xl md:text-5xl lg:text-7xl text-timber-wolf mb-2"
            >
                Luxury Facial Treatments
            </motion.h1>
            <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4 }}
                className="font-bodoni text-sm md:text-xl text-timber-wolf/80"
            >
                Rejuvenating therapies for radiant skin
            </motion.p>
        </div>
    </motion.div>
</div>


            {/* Main Content */}
            <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="max-w-7xl mx-auto px-4 py-16 md:py-24"
            >
                {/* Hydra Facial Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-start">
                        <div>
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                Hydra Facial
                            </h2>
                            <p className="text-eerie-black/80 mb-4">
                                The Hydra Facial is one of the most sought-after treatments in modern skincare — a medical-grade, non-invasive facial that delivers instant, visible results with zero downtime. Using patented vortex-fusion technology, it deeply cleanses, gently exfoliates, extracts impurities, and infuses the skin with powerful, medical-grade serums in a single session.
                            </p>
                            <p className="text-eerie-black/80 mb-6">
                                Unlike traditional facials that can leave skin red or irritated, the Hydra Facial is gentle enough for even sensitive skin while still delivering a thorough, professional-level treatment. Clients often describe their skin as instantly brighter, smoother, and more hydrated — the kind of glow that makes this a favourite before events, holidays, or as part of a regular skincare routine.
                            </p>

                            <h3 className="font-bodoni text-xl text-lapis-lazuli mb-3">How It Works</h3>
                            <p className="text-eerie-black/80 mb-4">
                                Each treatment follows a carefully designed three-step process tailored to your skin's needs:
                            </p>
                            <div className="space-y-3 mb-6">
                                {[
                                    'Cleanse & Peel — A gentle exfoliation removes dead skin cells and reveals a fresh layer of skin, preparing it for deep treatment.',
                                    'Extract & Hydrate — A painless vortex suction clears clogged pores of debris and oil, while simultaneously delivering hydrating serums.',
                                    'Fuse & Protect — Antioxidant-rich serums and peptides are infused deep into the skin to nourish, protect, and lock in your results.'
                                ].map((step) => (
                                    <div key={step} className="flex items-start gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0 mt-1" />
                                        <span className="text-eerie-black/80">{step}</span>
                                    </div>
                                ))}
                            </div>

                            <h3 className="font-bodoni text-xl text-lapis-lazuli mb-3">Key Benefits</h3>
                            <div className="space-y-3 mb-6">
                                {[
                                    'Instantly brighter, smoother, and more even-toned skin',
                                    'Deep hydration without heaviness or greasiness',
                                    'Reduced appearance of fine lines, enlarged pores, and congestion',
                                    'Improved skin texture and elasticity over a course of treatments',
                                    'Customizable booster serums for concerns like pigmentation, acne, or anti-aging'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-start gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0 mt-1" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>

                            <p className="text-eerie-black/70 italic">
                                Best for: All skin types — including sensitive, oily, dry, and mature skin. Ideal before special occasions or as a monthly maintenance treatment.
                            </p>
                        </div>
                        <div>
                            <div className="aspect-[4/3] bg-timber-wolf/10 mb-6">
                                <img 
                                    src={FACIAL_IMAGES.hydra} 
                                    alt="Hydra Facial treatment at Studio Omorfia"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6 border border-lapis-lazuli/20 rounded-lg bg-lapis-lazuli/5">
                                <h3 className="font-bodoni text-lg text-lapis-lazuli mb-3">What to Expect</h3>
                                <ul className="space-y-2 text-eerie-black/80 text-sm">
                                    <li>• Treatment time: approximately 90 minutes</li>
                                    <li>• No pain, no needles, and no recovery time</li>
                                    <li>• Makeup can be applied immediately after</li>
                                    <li>• Results are visible from the very first session</li>
                                    <li>• A course of 4–6 treatments is recommended for lasting improvement</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* LED Light Therapy Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1 aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src={FACIAL_IMAGES.led} 
                                alt="LED Light Therapy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                LED Light Therapy
                            </h2>
                            <p className="text-eerie-black/80 mb-6">
                                A non-invasive treatment using specific wavelengths of light to target acne, inflammation, and signs of aging at the cellular level.
                            </p>
                            <div className="space-y-3">
                                {[
                                    'Reduces acne and inflammation',
                                    'Stimulates collagen production',
                                    'Accelerates skin healing',
                                    'Non-invasive with no downtime',
                                    'Complements other facial treatments'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-center gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* Plasma Facial Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                Plasma Facials
                            </h2>
                            <p className="text-eerie-black/80 mb-6">
                                An advanced skin rejuvenation treatment using plasma energy to stimulate collagen, tighten skin, and address fine lines and pigmentation.
                            </p>
                            <div className="space-y-3">
                                {[
                                    'Skin tightening and lifting',
                                    'Reduces fine lines and wrinkles',
                                    'Improves skin texture',
                                    'Targets pigmentation',
                                    'Long-lasting rejuvenation results'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-center gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src={FACIAL_IMAGES.plasma} 
                                alt="Plasma Facial"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </motion.section>

                {/* Aromatic Facial Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                Aromatic Facial Treatment
                            </h2>
                            <p className="text-eerie-black/80 mb-6">
                                A rejuvenating skincare treatment that combines the therapeutic benefits of essential oils with traditional facial techniques for enhanced skin appearance and overall well-being.
                            </p>
                            <div className="space-y-3">
                                {[
                                    'Hydration and moisturization',
                                    'Stress reduction through aromatherapy',
                                    'Anti-aging benefits',
                                    'Deep detoxification',
                                    'Customized essential oil blend'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-center gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src={FACIAL_IMAGES.aromatic} 
                                alt="Aromatic Facial"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </motion.section>

                {/* Oxygenating Facial Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1 aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src="https://www.modernskintherapyca.com/wp-content/uploads/elementor/thumbs/oxygen-skin-therapy-image-q3zhfbavgwhlq7f4xlfvkt08dovepiiy4nundr359s.jpg" 
                                alt="Oxygenating Facial"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                Oxygenating Facial Treatment
                            </h2>
                            <p className="text-eerie-black/80 mb-6">
                                A cutting-edge skincare treatment designed to revitalize and rejuvenate your skin by infusing it with oxygen, promoting cellular renewal and a radiant appearance.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    'Enhanced oxygen flow',
                                    'Brightening effect',
                                    'Deep detoxification',
                                    'Intense hydration',
                                    'Improved skin elasticity'
                                ].map((benefit) => (
                                    <li key={benefit} className="flex items-center gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </motion.section>

                {/* Diamond Microdermabrasion Section - Centered without image */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="max-w-3xl mx-auto px-4">
                        <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                            Diamond Microdermabrasion Facial
                        </h2>
                        <p className="text-eerie-black/80 mb-6">
                            A non-invasive skincare treatment utilizing diamond-tipped tools to gently exfoliate the skin's surface, revealing a smoother, brighter complexion while promoting cellular renewal.
                        </p>
                        <div className="space-y-3">
                            {[
                                'Deep Exfoliation and unclogged pores',
                                'Enhanced skin tone and clarity',
                                'Reduced fine lines and wrinkles',
                                'Boosted collagen production',
                                'Customizable treatment approach'
                            ].map((benefit) => (
                                <div key={benefit} className="flex items-center gap-2">
                                    <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                    <span className="text-eerie-black/80">{benefit}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.section>

                {/* A-Lift Facial Section - Centered without image */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="max-w-3xl mx-auto px-4">
                        <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                            A-Lift Anti-Aging Facial
                        </h2>
                        <p className="text-eerie-black/80 mb-6">
                            A non-invasive facial treatment focusing on tightening and lifting the skin using advanced radio frequency technology to reduce the appearance of fine lines and wrinkles.
                        </p>
                        <div className="space-y-3">
                            {[
                                'Advanced RF technology application',
                                'Enhanced collagen production',
                                'Improved skin elasticity',
                                'Natural lifting effect',
                                'Non-surgical facelift alternative'
                            ].map((benefit) => (
                                <div key={benefit} className="flex items-center gap-2">
                                    <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                    <span className="text-eerie-black/80">{benefit}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.section>

                {/* Microneedling Facial Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1 aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src="https://diaminyaesthetics.com/cdn/shop/articles/microneedling_11_e0a98634-d14b-440e-afe0-affc895c6506.jpg?v=1726133735&width=1200" 
                                alt="Microneedling Facial"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                Microneedling (Face) Treatment
                            </h2>
                            <p className="text-eerie-black/80 mb-6">
                                A revolutionary skincare treatment using fine needles to create controlled micro-injuries, stimulating your skin's natural healing process and encouraging collagen production for smoother, firmer skin.
                            </p>
                            <div className="space-y-3">
                                {[
                                    'Improved skin texture and reduced pore size',
                                    'Reduced fine lines and wrinkles',
                                    'Diminished acne scarring',
                                    'Even skin tone and reduced hyperpigmentation',
                                    'Enhanced product absorption'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-center gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* Radio Frequency Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1 aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src="https://telegrafi.com/wp-content/uploads/2022/03/blog_redsculpt_1a8c61e8-1c9b-43d5-af10-b8868941e837_1200x798.jpg" 
                                alt="Radio Frequency Treatment"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                Radio Frequency (RF) Technology
                            </h2>
                            <p className="text-eerie-black/80 mb-6">
                                Radio frequency waves are applied to the skin to promote collagen production and increase skin elasticity by heating the deeper layers of the skin.
                            </p>
                            <div className="space-y-3">
                                {[
                                    'Promotes collagen production',
                                    'Increases skin elasticity',
                                    'Heats deeper skin layers',
                                    'Stimulates natural healing process',
                                    'Helps lift and firm the face over time'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-center gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* NIR Facial Rejuvenation Section */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="grid md:grid-cols-2 gap-12 items-start">
                        <div className="order-2 md:order-1 aspect-[4/3] bg-timber-wolf/10">
                            <img 
                                src={FACIAL_IMAGES.nir} 
                                alt="NIR Near Infrared facial rejuvenation at Studio Omorfia"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                                NIR Facial Rejuvenation
                            </h2>
                            <p className="text-eerie-black/80 mb-4">
                                Near Infrared (NIR) facial rejuvenation is an advanced, non-invasive treatment that uses infrared light energy to penetrate deep beneath the skin's surface — reaching the dermis where collagen and elastin are produced. Unlike treatments that only work on the outer layer, NIR stimulates your skin's natural repair processes from within.
                            </p>
                            <p className="text-eerie-black/80 mb-6">
                                The gentle heat generated by NIR light triggers a controlled thermal response in the skin, boosting circulation, accelerating cell renewal, and encouraging fresh collagen and elastin production. The result is firmer, smoother, and more youthful-looking skin — without surgery, needles, or downtime.
                            </p>

                            <h3 className="font-bodoni text-xl text-lapis-lazuli mb-3">Key Benefits</h3>
                            <div className="space-y-3 mb-6">
                                {[
                                    'Stimulates natural collagen and elastin production for long-term firmness',
                                    'Improves skin elasticity and reduces sagging along the jawline and neck',
                                    'Softens fine lines and wrinkles for a smoother complexion',
                                    'Enhances skin tone, texture, and overall radiance',
                                    'Completely non-invasive with no recovery period required'
                                ].map((benefit) => (
                                    <div key={benefit} className="flex items-start gap-2">
                                        <FaCheck className="text-lapis-lazuli flex-shrink-0 mt-1" />
                                        <span className="text-eerie-black/80">{benefit}</span>
                                    </div>
                                ))}
                            </div>

                            <p className="text-eerie-black/70 italic mb-6">
                                Best for: Mature skin, loss of firmness, fine lines, and clients seeking a natural anti-aging solution without injectables or surgery.
                            </p>
                        </div>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { area: 'Face', price: 6500 },
                            { area: 'Neck', price: 4500 },
                            { area: 'Face & Neck', price: 8500 },
                            { area: 'Décolleté', price: 12000 },
                        ].map((item) => (
                            <div
                                key={item.area}
                                className="p-5 rounded-lg border border-lapis-lazuli/20 bg-lapis-lazuli/5 text-center"
                            >
                                <h4 className="font-bodoni text-lg text-lapis-lazuli mb-1">{item.area}</h4>
                                <p className="text-eerie-black/80 font-medium">
                                    KES {item.price.toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 max-w-3xl">
                        <div className="p-6 border border-timber-wolf/20 rounded-lg">
                            <h3 className="font-bodoni text-lg text-lapis-lazuli mb-3">What to Expect</h3>
                            <ul className="space-y-2 text-eerie-black/80 text-sm">
                                <li>• A warm, relaxing sensation during treatment — most clients find it comfortable</li>
                                <li>• No needles, no anaesthetic, and no downtime afterward</li>
                                <li>• Skin may appear slightly flushed immediately after, settling within an hour</li>
                                <li>• Visible improvements build gradually over 3–6 sessions for optimal results</li>
                                <li>• Results continue to improve for weeks after each session as collagen remodelling takes place</li>
                            </ul>
                        </div>
                    </div>
                </motion.section>

                {/* Liver Spot Treatment */}
                <motion.section variants={itemVariants} className="mb-20">
                    <div className="max-w-3xl mx-auto px-4">
                        <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-6">
                            Liver Spot Treatment
                        </h2>
                        <p className="text-eerie-black/80 mb-6">
                            Age spots and liver spots — flat, brown patches caused by sun exposure and natural ageing — can be effectively targeted with our specialist pigmentation treatments. During your consultation, our skin specialist will assess your skin and recommend the most suitable approach, which may include NIR rejuvenation or complementary therapies for the best outcome.
                        </p>
                        <div className="space-y-3">
                            {[
                                'Targeted treatment for sun-induced pigmentation',
                                'Personalised plan based on skin type and spot depth',
                                'Can be combined with NIR for enhanced rejuvenation',
                                'Gradual, natural-looking improvement over a course of sessions'
                            ].map((benefit) => (
                                <div key={benefit} className="flex items-start gap-2">
                                    <FaCheck className="text-lapis-lazuli flex-shrink-0 mt-1" />
                                    <span className="text-eerie-black/80">{benefit}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.section>

                {/* Treatment Process */}
<motion.section variants={itemVariants} className="mt-20">
    <h2 className="font-bodoni text-3xl md:text-4xl text-lapis-lazuli mb-12 text-center">
        Our Treatment Process
    </h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
            {
                title: "Consultation",
                icon: FaRegUser,
                description: "Personalized skin analysis and treatment planning."
            },
            {
                title: "Preparation",
                icon: FaRegLightbulb,
                description: "Gentle cleansing and preparation of the skin."
            },
            {
                title: "Treatment",
                icon: FaClock,
                description: "Application of specialized techniques and products."
            },
            {
                title: "After-Care",
                icon: FaCheck,
                description: "Post-treatment care advice and product recommendations."
            }
        ].map((step) => (
            <div 
                key={step.title} 
                className="text-center p-6 border border-timber-wolf/20 hover:border-lapis-lazuli/30 transition-colors duration-300 rounded-lg"
            >
                <step.icon className="w-8 h-8 text-lapis-lazuli mx-auto mb-4" />
                <h3 className="font-bodoni text-xl text-eerie-black mb-2">{step.title}</h3>
                <p className="text-eerie-black/70">{step.description}</p>
            </div>
        ))}
    </div>
</motion.section>
            </motion.div>
        </div>
    );
};

export default Facial;